import {
  FREQUENCY,
  WEEKS_IN_A_YEAR,
  FORTNIGHTS_IN_A_YEAR,
  MONTHS_IN_A_YEAR,
  QUARTERS_IN_A_YEAR,
} from '@funnels/constants';

import formatToPrecision from './formatToPrecision';

/**
 * This module has logic for various calculations used for repayment calculation.
 * It returns to a precision of 2 decimals.
 */

/**
 * Converts an amount that is paid in one frequency to another
 *
 * EG: an amount of $200 per month is equivalent to $2400 per year.
 * Valid frequencies:
 *   wk: Week
 *   fn: Fortnight
 *   mo: Month
 *   yr: Year
 *
 * @param: amount: The amount to be converted.
 * @param: frequencyFrom: The current repayment frequency.
 * @param: frequencyTo: The repayment frequency that the amount should be converted to.
 *
 * @return number
 */
export function convert(amount: number, frequencyFrom: FREQUENCY, frequencyTo: FREQUENCY) {
  const frequencies = Object.values(FREQUENCY);
  if (!frequencies.includes(frequencyFrom) || !frequencies.includes(frequencyTo)) {
    return 0;
  }
  return converters.get(frequencyFrom).get(frequencyTo)(amount);
}

export function noOperation(a: number) {
  return formatToPrecision(a);
}

// Weekly
export function convertWeeklyToFortnightly(amount: number) {
  return formatToPrecision(amount * 2);
}
export function convertWeeklyToMonthly(amount: number) {
  return formatToPrecision(convertWeeklyToAnnual(amount) / MONTHS_IN_A_YEAR);
}
export function convertWeeklyToQuarterly(amount: number) {
  return formatToPrecision(convertWeeklyToAnnual(amount) / QUARTERS_IN_A_YEAR);
}
export function convertWeeklyToAnnual(amount: number) {
  return formatToPrecision(amount * WEEKS_IN_A_YEAR);
}
// Fortnightly
export function convertFortnightlyToWeekly(amount: number) {
  return formatToPrecision(amount / 2);
}
export function convertFortnightlyToMonthly(amount: number) {
  return formatToPrecision(convertFortnightlyToAnnual(amount) / MONTHS_IN_A_YEAR);
}
export function convertFortnightlyToQuarterly(amount: number) {
  return formatToPrecision(convertFortnightlyToAnnual(amount) / QUARTERS_IN_A_YEAR);
}
export function convertFortnightlyToAnnual(amount: number) {
  return formatToPrecision(amount * FORTNIGHTS_IN_A_YEAR);
}
// Monthly
export function convertMonthlyToWeekly(amount: number) {
  return formatToPrecision(convertMonthlyToAnnual(amount) / WEEKS_IN_A_YEAR);
}
export function convertMonthlyToFortnightly(amount: number) {
  return formatToPrecision(convertMonthlyToAnnual(amount) / FORTNIGHTS_IN_A_YEAR);
}
export function convertMonthlyToQuarterly(amount: number) {
  return formatToPrecision(convertMonthlyToAnnual(amount) / QUARTERS_IN_A_YEAR);
}
export function convertMonthlyToAnnual(amount: number) {
  return formatToPrecision(amount * MONTHS_IN_A_YEAR);
}
// Quarterly
export function convertQuarterlyToWeekly(amount: number) {
  return formatToPrecision(convertQuarterlyToAnnual(amount) / WEEKS_IN_A_YEAR);
}
export function convertQuarterlyToFortnightly(amount: number) {
  return formatToPrecision(convertQuarterlyToAnnual(amount) / FORTNIGHTS_IN_A_YEAR);
}
export function convertQuarterlyToMonthly(amount: number) {
  return formatToPrecision(convertQuarterlyToAnnual(amount) / MONTHS_IN_A_YEAR);
}
export function convertQuarterlyToAnnual(amount: number) {
  return formatToPrecision(amount * QUARTERS_IN_A_YEAR);
}
// Annually
export function convertAnnualToWeekly(amount: number) {
  return formatToPrecision(amount / WEEKS_IN_A_YEAR);
}
export function convertAnnualToFortnightly(amount: number) {
  return formatToPrecision(amount / FORTNIGHTS_IN_A_YEAR);
}
export function convertAnnualToMonthly(amount: number) {
  return formatToPrecision(amount / MONTHS_IN_A_YEAR);
}
export function convertAnnualToQuarterly(amount: number) {
  return formatToPrecision(amount / QUARTERS_IN_A_YEAR);
}

const converters = new Map();

converters.set(FREQUENCY.WEEKLY, new Map());
converters.get(FREQUENCY.WEEKLY).set(FREQUENCY.WEEKLY, noOperation);
converters.get(FREQUENCY.WEEKLY).set(FREQUENCY.FORTNIGHTLY, convertWeeklyToFortnightly);
converters.get(FREQUENCY.WEEKLY).set(FREQUENCY.MONTHLY, convertWeeklyToMonthly);
converters.get(FREQUENCY.WEEKLY).set(FREQUENCY.QUARTERLY, convertWeeklyToQuarterly);
converters.get(FREQUENCY.WEEKLY).set(FREQUENCY.YEARLY, convertWeeklyToAnnual);

converters.set(FREQUENCY.FORTNIGHTLY, new Map());
converters.get(FREQUENCY.FORTNIGHTLY).set(FREQUENCY.WEEKLY, convertFortnightlyToWeekly);
converters.get(FREQUENCY.FORTNIGHTLY).set(FREQUENCY.FORTNIGHTLY, noOperation);
converters.get(FREQUENCY.FORTNIGHTLY).set(FREQUENCY.MONTHLY, convertFortnightlyToMonthly);
converters.get(FREQUENCY.FORTNIGHTLY).set(FREQUENCY.QUARTERLY, convertFortnightlyToQuarterly);
converters.get(FREQUENCY.FORTNIGHTLY).set(FREQUENCY.YEARLY, convertFortnightlyToAnnual);

converters.set(FREQUENCY.MONTHLY, new Map());
converters.get(FREQUENCY.MONTHLY).set(FREQUENCY.WEEKLY, convertMonthlyToWeekly);
converters.get(FREQUENCY.MONTHLY).set(FREQUENCY.FORTNIGHTLY, convertMonthlyToFortnightly);
converters.get(FREQUENCY.MONTHLY).set(FREQUENCY.MONTHLY, noOperation);
converters.get(FREQUENCY.MONTHLY).set(FREQUENCY.QUARTERLY, convertMonthlyToQuarterly);
converters.get(FREQUENCY.MONTHLY).set(FREQUENCY.YEARLY, convertMonthlyToAnnual);

converters.set(FREQUENCY.QUARTERLY, new Map());
converters.get(FREQUENCY.QUARTERLY).set(FREQUENCY.WEEKLY, convertQuarterlyToWeekly);
converters.get(FREQUENCY.QUARTERLY).set(FREQUENCY.FORTNIGHTLY, convertQuarterlyToFortnightly);
converters.get(FREQUENCY.QUARTERLY).set(FREQUENCY.MONTHLY, convertQuarterlyToMonthly);
converters.get(FREQUENCY.QUARTERLY).set(FREQUENCY.QUARTERLY, noOperation);
converters.get(FREQUENCY.QUARTERLY).set(FREQUENCY.YEARLY, convertQuarterlyToAnnual);

converters.set(FREQUENCY.YEARLY, new Map());
converters.get(FREQUENCY.YEARLY).set(FREQUENCY.WEEKLY, convertAnnualToWeekly);
converters.get(FREQUENCY.YEARLY).set(FREQUENCY.FORTNIGHTLY, convertAnnualToFortnightly);
converters.get(FREQUENCY.YEARLY).set(FREQUENCY.MONTHLY, convertAnnualToMonthly);
converters.get(FREQUENCY.YEARLY).set(FREQUENCY.QUARTERLY, convertAnnualToQuarterly);
converters.get(FREQUENCY.YEARLY).set(FREQUENCY.YEARLY, noOperation);

/**
 * Return the number of payments to be made given the term and frequency of loan
 *
 * @param {number} loanTerm: The length of loan in months
 * @param {string} repaymentFrequency: Frequency of payments
 *
 * @return {number}
 */
export function getNumOfRepayments(loanTerm: number, repaymentFrequency = FREQUENCY.MONTHLY) {
  switch (repaymentFrequency) {
    case FREQUENCY.WEEKLY:
      return (loanTerm / MONTHS_IN_A_YEAR) * WEEKS_IN_A_YEAR;

    case FREQUENCY.FORTNIGHTLY:
      return (loanTerm / MONTHS_IN_A_YEAR) * FORTNIGHTS_IN_A_YEAR;

    case FREQUENCY.QUARTERLY:
      return (loanTerm / MONTHS_IN_A_YEAR) * QUARTERS_IN_A_YEAR;

    case FREQUENCY.YEARLY:
      return loanTerm / MONTHS_IN_A_YEAR;

    case FREQUENCY.MONTHLY:
    default:
      return loanTerm;
  }
}

/**
 * Convert annual rate to rate for repayment frequency
 *
 * @param {number} rate: annual interest rate
 * @param {string} repaymentFrequency: Frequency of payments
 *
 * @return {number}
 */
export function getRateForFrequency(rate: number, repaymentFrequency: FREQUENCY) {
  switch (repaymentFrequency) {
    case FREQUENCY.WEEKLY:
      return rate / WEEKS_IN_A_YEAR;

    case FREQUENCY.FORTNIGHTLY:
      return rate / FORTNIGHTS_IN_A_YEAR;

    case FREQUENCY.MONTHLY:
      return rate / MONTHS_IN_A_YEAR;

    case FREQUENCY.QUARTERLY:
      return rate / QUARTERS_IN_A_YEAR;

    case FREQUENCY.YEARLY:
    default:
      return rate;
  }
}

/**
 * Convert value based on frequency to its annual value
 *
 * @param {number} value
 * @param {string} frequency
 *
 * @return {number}
 */
export function getAnnualValueFromFrequency(value: number, frequency: FREQUENCY) {
  switch (frequency) {
    case FREQUENCY.WEEKLY:
      return value * WEEKS_IN_A_YEAR;

    case FREQUENCY.FORTNIGHTLY:
      return value * FORTNIGHTS_IN_A_YEAR;

    case FREQUENCY.MONTHLY:
      return value * MONTHS_IN_A_YEAR;

    case FREQUENCY.QUARTERLY:
      return value * QUARTERS_IN_A_YEAR;

    case FREQUENCY.YEARLY:
    default:
      return value;
  }
}

/**
 * Convert years and months into just months
 *
 * @param {number} years: number of years
 * @param {number} months: number of months
 *
 * @return {number}
 */
export function convertYearsMonthsToMonths(years: number = 0, months: number = 0) {
  return years * MONTHS_IN_A_YEAR + months;
}

export function convertYearsMonthsToYears(years: number = 0, months: number = 0) {
  return years + formatToPrecision(months / 12);
}

/**
 * Convert months to years
 *
 * @param {number} months: number of months
 *
 * @return {number}
 */
export function convertMonthsToYears(months: number = 0) {
  return months / MONTHS_IN_A_YEAR;
}

export function convertYearsToMonths(value: number = 0) {
  const { years, months } = convertYearsToYearsMonths(value);

  return years * 12 + months;
}

export function convertYearsToYearsMonths(value: number) {
  const years = Math.floor(value);
  const months = Math.round((value - years) * 12);

  if (months === 12) {
    return { years: years + 1, months: 0 };
  }

  return { years, months };
}
